import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type { Reader, ReaderFilesSummary, ReaderPersonNotesSummary } from '@/api/client';
import { ReaderTableCellCommentShell } from '@/features/project-cell-comments/ReaderTableCellCommentShell';
import { AttachFileIcon, DescriptionIcon } from '@/components/ui/Icons';
import { IconActionButton } from '@/components/ProjectsTable/IconActionButton';
import { ReadersTableRowMenu } from '@/components/ReadersTable/ReadersTableRowMenu';
import { ReaderCatalogCooperationStateCell } from '@/components/ReadersTable/ReaderCatalogCooperationStateCell';
import { ReaderCatalogNameCell } from '@/components/ReadersTable/ReaderCatalogNameCell';
import { ReaderCatalogTagsCell } from '@/components/ReadersTable/ReaderCatalogTagsCell';
import { ReaderCatalogEditableCell } from '@/components/ReadersTable/ReaderCatalogEditableCell';
import { ReaderContactsComboCell } from '@/components/ReadersTable/ReaderContactsComboCell';
import { ReaderTagsCatalogModal } from '@/components/ReaderTagsCatalogModal/ReaderTagsCatalogModal';
import { VirtualRowRemeasureContext } from '@/components/ProjectsTable/VirtualRowRemeasureContext';
import { nameNoteSingleLine } from '@/utils/nameNote';
import { sortReaderCatalogPrices, formatReaderCatalogPriceLine } from '@/utils/formatNumber';
import {
  getReaderCooperationRowBackground,
  normalizeReaderCooperationState,
} from '@/utils/readerCooperationStates';
import { READER_CATALOG_SUMMARY_KEYS, READER_COLUMNS } from '@/components/ReadersTable/readersTableLayout';
import { readerCatalogTableDisplayName } from '@/utils/readerCatalogTableDisplayName';
import {
  highlightPlainText,
  SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG,
} from '@/utils/highlightSearchTerms';

function searchHighlightWordsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function readersPricesEqual(a: Reader['reader_prices'], b: Reader['reader_prices']): boolean {
  const la = sortReaderCatalogPrices(a);
  const lb = sortReaderCatalogPrices(b);
  if (la.length !== lb.length) return false;
  for (let i = 0; i < la.length; i++) {
    const x = la[i]!;
    const y = lb[i]!;
    if (
      x.id !== y.id ||
      String(x.price) !== String(y.price) ||
      (x.price_name?.trim() || '') !== (y.price_name?.trim() || '') ||
      x.display_order !== y.display_order
    ) {
      return false;
    }
  }
  return true;
}

function EmptyNa() {
  return (
    <div className="readers-table-catalog__na-wrap">
      <span className="projects-table__empty-value">N/A</span>
    </div>
  );
}

function ReaderCell({
  reader,
  columnId,
  summary,
  cellClassName,
  children,
}: {
  reader: Reader;
  columnId: string;
  summary: Record<string, number> | undefined;
  cellClassName: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cellClassName}>
      <ReaderTableCellCommentShell
        readerPersonId={reader.id}
        readerName={readerCatalogTableDisplayName(reader)}
        columnId={columnId}
        commentCount={summary?.[columnId] ?? 0}
      >
        {children}
      </ReaderTableCellCommentShell>
    </div>
  );
}

function readersTagsEqual(a: Reader['tags'], b: Reader['tags']): boolean {
  const la = a ?? [];
  const lb = b ?? [];
  if (la.length !== lb.length) return false;
  for (let i = 0; i < la.length; i += 1) {
    const x = la[i]!;
    const y = lb[i]!;
    if (x.id !== y.id || x.name !== y.name || x.color !== y.color || x.tag_group !== y.tag_group) return false;
  }
  return true;
}

function readersLinksEqual(a: Reader['links'], b: Reader['links']): boolean {
  const la = a ?? [];
  const lb = b ?? [];
  if (la.length !== lb.length) return false;
  for (let i = 0; i < la.length; i += 1) {
    const x = la[i]!;
    const y = lb[i]!;
    if (
      x.id !== y.id ||
      x.link !== y.link ||
      (x.link_name?.trim() || '') !== (y.link_name?.trim() || '')
    ) {
      return false;
    }
  }
  return true;
}

function readerCatalogDataEqual(a: Reader, b: Reader): boolean {
  if (a.id !== b.id) return false;
  if (String(a.first_name ?? '') !== String(b.first_name ?? '')) return false;
  if (String(a.last_name ?? '') !== String(b.last_name ?? '')) return false;
  if (a.name !== b.name) return false;
  if (nameNoteSingleLine(a.name_note) !== nameNoteSingleLine(b.name_note)) return false;
  if (!readersPricesEqual(a.reader_prices, b.reader_prices)) return false;
  if (String(a.email ?? '').trim() !== String(b.email ?? '').trim()) return false;
  if (String(a.phone ?? '').trim() !== String(b.phone ?? '').trim()) return false;
  if ((a.studio?.id ?? null) !== (b.studio?.id ?? null)) return false;
  if ((a.legal_entity?.id ?? null) !== (b.legal_entity?.id ?? null)) return false;
  if (normalizeReaderCooperationState(a.cooperation_state) !== normalizeReaderCooperationState(b.cooperation_state))
    return false;
  if (!readersTagsEqual(a.tags, b.tags)) return false;
  return readersLinksEqual(a.links, b.links);
}

function cellCommentSummaryEqual(
  a: Record<string, number> | undefined,
  b: Record<string, number> | undefined
): boolean {
  for (const k of READER_CATALOG_SUMMARY_KEYS) {
    if ((a?.[k] ?? 0) !== (b?.[k] ?? 0)) return false;
  }
  return true;
}

function personNotesSummaryEqual(
  a: ReaderPersonNotesSummary | undefined,
  b: ReaderPersonNotesSummary | undefined
): boolean {
  return (a?.noteCount ?? 0) === (b?.noteCount ?? 0) && (a?.recentNoteCount ?? 0) === (b?.recentNoteCount ?? 0);
}

function readerFilesSummaryEqual(
  a: ReaderFilesSummary | undefined,
  b: ReaderFilesSummary | undefined
): boolean {
  return (a?.fileCount ?? 0) === (b?.fileCount ?? 0) && (a?.recentFileCount ?? 0) === (b?.recentFileCount ?? 0);
}

export interface ReadersTableRowProps {
  reader: Reader;
  /** Слова из строки поиска каталога (подсветка в ячейках). */
  searchHighlightWords: string[];
  cellCommentSummary: Record<string, number> | undefined;
  /** Сводка заметок персоны (батч), как projectResourceSummaries для колонки заметок. */
  personNotesSummary: ReaderPersonNotesSummary | undefined;
  /** Первичная загрузка саммари таблицы — спиннер на кнопках заметок и файлов. */
  notesSummaryLoading: boolean;
  /** Префетч перед открытием попапа заметок. */
  notesOpening: boolean;
  readerFilesSummary: ReaderFilesSummary | undefined;
  /** Префетч перед открытием попапа файлов. */
  filesOpening: boolean;
  rowBusy: boolean;
  top: number;
  dataIndex: number;
  measureRef: (el: HTMLDivElement | null) => void;
  gridTemplateColumns: string;
  showReaderPrice?: boolean;
  onRequestDelete: (reader: Reader) => void;
  onOpenNotes: (reader: Reader, anchorEl: HTMLElement) => void;
  onOpenFiles: (reader: Reader, anchorEl: HTMLElement) => void;
  onOpenPersonManage?: (personId: string) => void;
}

function areReadersTableRowPropsEqual(
  prev: Readonly<ReadersTableRowProps>,
  next: Readonly<ReadersTableRowProps>
): boolean {
  if (prev.reader.id !== next.reader.id) return false;
  if (prev.top !== next.top) return false;
  if (prev.dataIndex !== next.dataIndex) return false;
  if (prev.rowBusy !== next.rowBusy) return false;
  if (prev.gridTemplateColumns !== next.gridTemplateColumns) return false;
  if (prev.showReaderPrice !== next.showReaderPrice) return false;
  if (!readerCatalogDataEqual(prev.reader, next.reader)) return false;
  if (!cellCommentSummaryEqual(prev.cellCommentSummary, next.cellCommentSummary)) return false;
  if (!personNotesSummaryEqual(prev.personNotesSummary, next.personNotesSummary)) return false;
  if (!readerFilesSummaryEqual(prev.readerFilesSummary, next.readerFilesSummary)) return false;
  if (prev.notesSummaryLoading !== next.notesSummaryLoading) return false;
  if (prev.notesOpening !== next.notesOpening) return false;
  if (prev.filesOpening !== next.filesOpening) return false;
  if (prev.measureRef !== next.measureRef) return false;
  if (!searchHighlightWordsEqual(prev.searchHighlightWords, next.searchHighlightWords)) return false;
  if (prev.onRequestDelete !== next.onRequestDelete) return false;
  if (prev.onOpenNotes !== next.onOpenNotes) return false;
  if (prev.onOpenFiles !== next.onOpenFiles) return false;
  if (prev.onOpenPersonManage !== next.onOpenPersonManage) return false;
  return true;
}

export const ReadersTableRow = memo(function ReadersTableRow({
  reader,
  searchHighlightWords,
  cellCommentSummary,
  personNotesSummary,
  notesSummaryLoading,
  notesOpening,
  readerFilesSummary,
  filesOpening,
  rowBusy,
  top,
  dataIndex,
  measureRef,
  gridTemplateColumns,
  showReaderPrice = true,
  onRequestDelete,
  onOpenNotes,
  onOpenFiles,
  onOpenPersonManage,
}: ReadersTableRowProps) {
  const sortedPrices = useMemo(() => sortReaderCatalogPrices(reader.reader_prices), [reader.reader_prices]);
  const studioName = reader.studio?.name?.trim() ?? '';
  const studioDisplay =
    searchHighlightWords.length > 0 && studioName
      ? highlightPlainText(studioName, searchHighlightWords, SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG)
      : studioName;
  const legalEntityName = reader.legal_entity?.name?.trim() ?? '';
  const legalEntityDisplay =
    searchHighlightWords.length > 0 && legalEntityName
      ? highlightPlainText(legalEntityName, searchHighlightWords, SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG)
      : legalEntityName;
  const hasNotes = (personNotesSummary?.noteCount ?? 0) > 0;
  const notesNewCount = personNotesSummary?.recentNoteCount ?? 0;
  const hasFiles = (readerFilesSummary?.fileCount ?? 0) > 0;
  const filesNewCount = readerFilesSummary?.recentFileCount ?? 0;

  const coopNorm = normalizeReaderCooperationState(reader.cooperation_state);
  const rowBg = getReaderCooperationRowBackground(reader.cooperation_state);
  const stopListRow = coopNorm === 'В стоп-листе';

  const elRef = useRef<HTMLDivElement | null>(null);
  const combinedRef = useCallback(
    (el: HTMLDivElement | null) => {
      elRef.current = el;
      measureRef(el);
    },
    [measureRef]
  );

  const remeasureRow = useCallback(() => {
    if (elRef.current) measureRef(elRef.current);
  }, [measureRef]);

  useLayoutEffect(() => {
    if (elRef.current) measureRef(elRef.current);
  }, [dataIndex, measureRef, reader]);

  const handleNotesClick = useCallback(
    (anchorEl: HTMLElement) => {
      onOpenNotes(reader, anchorEl);
    },
    [onOpenNotes, reader]
  );

  const handleFilesClick = useCallback(
    (anchorEl: HTMLElement) => {
      onOpenFiles(reader, anchorEl);
    },
    [onOpenFiles, reader]
  );

  const [tagsEditorOpen, setTagsEditorOpen] = useState(false);
  const openTagsEditor = useCallback(() => setTagsEditorOpen(true), []);

  return (
    <div
      ref={combinedRef}
      data-index={dataIndex}
      data-reader-id={reader.id}
      className="projects-table__virtual-row projects-table__row-wrapper"
      style={{ top: `${top}px`, transform: 'none' }}
      role="row"
    >
      <VirtualRowRemeasureContext.Provider value={remeasureRow}>
      <div
        className={clsx('projects-table__row', stopListRow && 'readers-table-catalog__row_stoplist')}
        style={{
          gridTemplateColumns,
          backgroundColor: rowBg,
          ['--row-bg' as string]: rowBg,
        }}
      >
        <div className={READER_COLUMNS[0].cellClassName}>
          <ReadersTableRowMenu
            reader={reader}
            disabled={rowBusy}
            onRequestDelete={onRequestDelete}
            onOpenPersonManage={onOpenPersonManage}
            onOpenTagsCatalogModal={openTagsEditor}
          />
        </div>
        <div className={READER_COLUMNS[1].cellClassName}>
          <ReaderCatalogCooperationStateCell reader={reader} disabled={rowBusy} />
        </div>
        <ReaderCell
          reader={reader}
          columnId="name"
          summary={cellCommentSummary}
          cellClassName={READER_COLUMNS[2].cellClassName}
        >
          <ReaderCatalogNameCell
            reader={reader}
            disabled={rowBusy}
            searchHighlightWords={searchHighlightWords}
          />
        </ReaderCell>
        <ReaderCell
          reader={reader}
          columnId="studio"
          summary={cellCommentSummary}
          cellClassName={READER_COLUMNS[3].cellClassName}
        >
          <ReaderCatalogEditableCell reader={reader} field="studio" disabled={rowBusy}>
            {studioName ? <div className="projects-table__contractors-cell">{studioDisplay}</div> : <EmptyNa />}
          </ReaderCatalogEditableCell>
        </ReaderCell>
        <ReaderCell
          reader={reader}
          columnId="legal_entity"
          summary={cellCommentSummary}
          cellClassName={READER_COLUMNS[4].cellClassName}
        >
          <ReaderCatalogEditableCell reader={reader} field="legal_entity" disabled={rowBusy}>
            {legalEntityName ? (
              <div className="projects-table__contractors-cell">{legalEntityDisplay}</div>
            ) : (
              <EmptyNa />
            )}
          </ReaderCatalogEditableCell>
        </ReaderCell>
        {showReaderPrice ? (
        <ReaderCell
          reader={reader}
          columnId="reader_price"
          summary={cellCommentSummary}
          cellClassName={READER_COLUMNS[5].cellClassName}
        >
          <ReaderCatalogEditableCell reader={reader} field="reader_price" disabled={rowBusy}>
            {sortedPrices.length > 0 ? (
              <div className="readers-table-catalog__price-lines">
                {sortedPrices.map((p) => (
                  <div key={p.id} className="projects-table__base-cell readers-table-catalog__price-line">
                    {formatReaderCatalogPriceLine(p)}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyNa />
            )}
          </ReaderCatalogEditableCell>
        </ReaderCell>
        ) : null}
        <ReaderCell
          reader={reader}
          columnId="contacts"
          summary={cellCommentSummary}
          cellClassName={READER_COLUMNS[6].cellClassName}
        >
          <ReaderContactsComboCell reader={reader} disabled={rowBusy} />
        </ReaderCell>
        <ReaderCell
          reader={reader}
          columnId="demo"
          summary={cellCommentSummary}
          cellClassName={READER_COLUMNS[7].cellClassName}
        >
          <ReaderCatalogEditableCell reader={reader} field="demo" disabled={rowBusy} />
        </ReaderCell>
        <div className={READER_COLUMNS[8].cellClassName}>
          <div className="projects-table__sessions-cell projects-table__menu-combo-cell projects-table__status-flags-combo-cell">
            <ReaderTableCellCommentShell
              readerPersonId={reader.id}
              readerName={readerCatalogTableDisplayName(reader)}
              columnId="files"
              commentCount={cellCommentSummary?.files ?? 0}
            >
              <IconActionButton
                icon={<AttachFileIcon size={18} />}
                btnClass="projects-table__files-btn"
                hasContent={hasFiles}
                isLoading={notesSummaryLoading || filesOpening}
                newCount={filesNewCount}
                newCountAriaLabel="Новых файлов"
                ariaLabel="Открыть файлы чтеца"
                onClick={handleFilesClick}
              />
            </ReaderTableCellCommentShell>
            <ReaderTableCellCommentShell
              readerPersonId={reader.id}
              readerName={readerCatalogTableDisplayName(reader)}
              columnId="notes"
              commentCount={cellCommentSummary?.notes ?? 0}
            >
              <IconActionButton
                icon={<DescriptionIcon size={18} />}
                btnClass="projects-table__notes-btn"
                hasContent={hasNotes}
                isLoading={notesSummaryLoading || notesOpening}
                newCount={notesNewCount}
                newCountAriaLabel="Новых заметок"
                ariaLabel="Открыть заметки чтеца"
                onClick={handleNotesClick}
              />
            </ReaderTableCellCommentShell>
          </div>
        </div>
        <ReaderCell
          reader={reader}
          columnId="tags"
          summary={cellCommentSummary}
          cellClassName={READER_COLUMNS[9].cellClassName}
        >
          <ReaderCatalogTagsCell reader={reader} disabled={rowBusy} onEditTags={openTagsEditor} />
        </ReaderCell>
      </div>
      </VirtualRowRemeasureContext.Provider>
      <ReaderTagsCatalogModal open={tagsEditorOpen} onClose={() => setTagsEditorOpen(false)} />
    </div>
  );
}, areReadersTableRowPropsEqual);
